import { DEFENSE_TUNING } from './defense';
import { FOOTWORK_TUNING } from './footwork';
import { PUNCH_TUNING } from './punch';

/**
 * Runtime-adjustable recogniser thresholds.
 *
 * These were module constants, which meant tuning required an edit, a rebuild
 * and a redeploy — a ~60 second loop for a change you need to evaluate by
 * standing in front of a camera and repeating a movement. That is the wrong
 * feedback loop for the one part of the system that genuinely cannot be
 * designed on paper.
 *
 * Every threshold here is expressed in calibrated units (shoulder widths,
 * fractions of the player's own arm length, sw/second) so the numbers mean the
 * same thing for every body. That is what makes a *shared default* possible at
 * all — and also what makes a per-user override meaningful when the default is
 * wrong for someone.
 */

export interface Tunable {
  id: string;
  label: string;
  group: 'punch' | 'defence' | 'footwork';
  /** What failing this gate looks like, in plain language. */
  help: string;
  unit: string;
  min: number;
  max: number;
  step: number;
  get(): number;
  set(v: number): void;
}

const T = (
  id: string,
  label: string,
  group: Tunable['group'],
  unit: string,
  min: number,
  max: number,
  step: number,
  get: () => number,
  set: (v: number) => void,
  help: string,
): Tunable => ({ id, label, group, unit, min, max, step, get, set, help });

export const TUNABLES: Tunable[] = [
  // --- punch onset ---------------------------------------------------------
  T('punch.rateOn', 'Punch trigger speed', 'punch', '/s', 0.4, 4, 0.05,
    () => PUNCH_TUNING.rateOn, (v) => (PUNCH_TUNING.rateOn = v),
    'How fast your arm must be extending to start a punch. Lower it if slow or short punches are ignored.'),
  T('punch.minOnsetSpeed', 'Punch trigger hand speed', 'punch', 'sw/s', 0.4, 5, 0.1,
    () => PUNCH_TUNING.minOnsetSpeed, (v) => (PUNCH_TUNING.minOnsetSpeed = v),
    'Minimum glove speed at the start of a punch. Lower it if your punches are not registering at all.'),
  T('punch.minTravel', 'Minimum punch travel', 'punch', 'arm', 0.05, 0.4, 0.01,
    () => PUNCH_TUNING.minTravel, (v) => (PUNCH_TUNING.minTravel = v),
    'How far the arm must extend, as a fraction of your reach. Lower it if short/compact punches are dropped.'),
  T('punch.minPeakSpeed', 'Minimum punch peak speed', 'punch', 'sw/s', 0.8, 8, 0.1,
    () => PUNCH_TUNING.minPeakSpeed, (v) => (PUNCH_TUNING.minPeakSpeed = v),
    'Peak glove speed required for a punch to count. Lower it if you are shadow-boxing gently.'),
  T('punch.refractoryMs', 'Min gap between punches', 'punch', 'ms', 40, 400, 10,
    () => PUNCH_TUNING.refractoryMs, (v) => (PUNCH_TUNING.refractoryMs = v),
    'Raise it if one punch registers twice. Lower it if fast doubles only count once.'),
  T('punch.straightRatio', 'Straight-vs-hook bias', 'punch', '', 0.3, 0.9, 0.02,
    () => PUNCH_TUNING.straightRatio, (v) => (PUNCH_TUNING.straightRatio = v),
    'How forward a punch must travel to read as a straight rather than a hook.'),

  // --- defence -------------------------------------------------------------
  T('defence.enter', 'Head movement trigger', 'defence', 'sw', 0.06, 0.5, 0.01,
    () => DEFENSE_TUNING.enter, (v) => (DEFENSE_TUNING.enter = v),
    'How far your head must move to count as a slip/duck/pull. THE most common cause of "it ignores my head movement".'),
  T('defence.minSpeed', 'Head movement trigger speed', 'defence', 'sw/s', 0.2, 3, 0.05,
    () => DEFENSE_TUNING.minSpeed, (v) => (DEFENSE_TUNING.minSpeed = v),
    'How fast your head must move. Lower it if slow, deliberate slips are ignored.'),
  T('defence.fullDepth', 'Full commitment depth', 'defence', 'sw', 0.2, 0.9, 0.01,
    () => DEFENSE_TUNING.fullDepth, (v) => (DEFENSE_TUNING.fullDepth = v),
    'Head displacement that counts as a fully committed evasion. Lower it if your dodges feel weak.'),
  T('defence.exit', 'Head movement release', 'defence', 'sw', 0.03, 0.3, 0.01,
    () => DEFENSE_TUNING.exit, (v) => (DEFENSE_TUNING.exit = v),
    'How far back toward centre ends the movement. Must stay below the trigger.'),

  // --- footwork ------------------------------------------------------------
  T('footwork.lateralEnter', 'Side-step distance', 'footwork', 'sw', 0.1, 0.8, 0.02,
    () => FOOTWORK_TUNING.lateralEnter, (v) => (FOOTWORK_TUNING.lateralEnter = v),
    'How far you must move sideways to register a step.'),
  T('footwork.minSpeed', 'Step trigger speed', 'footwork', 'sw/s', 0.2, 3, 0.05,
    () => FOOTWORK_TUNING.minSpeed, (v) => (FOOTWORK_TUNING.minSpeed = v),
    'Peak movement speed required for a step.'),
];

const KEY = 'vs.tuning.v1';

export function saveTuning(): void {
  try {
    const out: Record<string, number> = {};
    for (const t of TUNABLES) out[t.id] = t.get();
    localStorage.setItem(KEY, JSON.stringify(out));
  } catch {
    /* private browsing */
  }
}

export function loadTuning(): boolean {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return false;
    const obj = JSON.parse(raw) as Record<string, number>;
    let n = 0;
    for (const t of TUNABLES) {
      const v = obj[t.id];
      if (typeof v === 'number' && Number.isFinite(v)) {
        t.set(Math.min(t.max, Math.max(t.min, v)));
        n++;
      }
    }
    return n > 0;
  } catch {
    return false;
  }
}

const DEFAULTS: Record<string, number> = Object.fromEntries(TUNABLES.map((t) => [t.id, t.get()]));

export function resetTuning(): void {
  for (const t of TUNABLES) t.set(DEFAULTS[t.id]);
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
}

export function tuningIsDefault(): boolean {
  return TUNABLES.every((t) => Math.abs(t.get() - DEFAULTS[t.id]) < 1e-9);
}
